import { Link } from "react-router-dom";
import { Card } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { PRICING_PLANS, formatMonthlyPrice } from "@/lib/pricing-config";
import { SITE } from "@/lib/site-config";

export function Pricing() {
  return (
    <section id="pricing" className="border-t border-line bg-paper-raised py-20">
      <div className="container-page">
        <h2 className="font-display text-3xl text-ink">Pricing</h2>
        <p className="mt-3 max-w-prose text-ink-soft">
          Simple, per-location pricing. Every plan includes automatic missed-call follow-up;
          higher tiers add AI qualification, custom rules, and analytics.
        </p>
        <div className="mt-10 grid gap-6 sm:grid-cols-3">
          {PRICING_PLANS.map((plan) => (
            <Card
              key={plan.id}
              className={`flex flex-col p-6 ${plan.highlighted ? "ring-2 ring-brand-500" : ""}`}
            >
              {plan.highlighted && <Badge tone="signal">Most popular</Badge>}
              <p className="mt-3 font-medium text-ink">{plan.name}</p>
              <p className="mt-2 font-display text-3xl text-ink">
                {formatMonthlyPrice(plan)}
                {plan.monthlyPrice !== null && (
                  <span className="ml-1 text-sm font-normal text-ink-soft">{plan.priceSuffix}</span>
                )}
              </p>
              <p className="mt-2 text-sm text-ink-soft">{plan.description}</p>
              <ul className="mt-5 flex-1 space-y-2">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm text-ink-soft">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-500" />
                    {feature}
                  </li>
                ))}
              </ul>
              {plan.ctaHref.startsWith("/") ? (
                <Link to={plan.ctaHref} className="mt-6">
                  <Button variant={plan.highlighted ? "primary" : "secondary"} className="w-full">
                    {plan.ctaLabel}
                  </Button>
                </Link>
              ) : (
                <a href={plan.ctaHref} className="mt-6 block">
                  <Button variant={plan.highlighted ? "primary" : "secondary"} className="w-full">
                    {plan.ctaLabel}
                  </Button>
                </a>
              )}
            </Card>
          ))}
        </div>
        <p className="mt-6 text-xs text-ink-soft">
          Prices shown are current list prices and may change \u2014 see your agreement for what
          applies to your account.
        </p>
      </div>
    </section>
  );
}

const faqs = [
  {
    q: "How does it work?",
    a: "When a call to your business goes unanswered, Callback automatically sends the caller a text asking what they need. They reply, the conversation continues over SMS, and the reply is checked against your business's rules to see if it's a qualified lead. Qualified leads land in your dashboard with the full conversation attached.",
  },
  {
    q: "Does it replace my employees?",
    a: "No. Callback handles the moment right after a missed call, before anyone would have followed up anyway. Your team still makes the sale, does the job, and talks to the customer \u2014 Callback just makes sure that conversation gets started instead of never happening.",
  },
  {
    q: "Can I customize the SMS?",
    a: "Yes. The wording, tone, and timing of the follow-up text are configurable per business, so it reads like it's actually from you.",
  },
  {
    q: "Can I control what the AI says?",
    a: "Yes. You set the qualification rules \u2014 what questions get asked, what counts as a qualified lead, and what topics are off-limits. The AI operates within those rules rather than improvising.",
  },
  {
    q: "What happens when the AI is unsure?",
    a: "If a reply doesn't clearly match your rules, the conversation is flagged for a human to review instead of being guessed at or auto-qualified. You choose how those edge cases get routed in your automation settings.",
  },
  {
    q: "Can I see the conversations?",
    a: "Yes. Every text exchange is saved against the lead it produced and is visible in your dashboard in real time \u2014 nothing happens off the record.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. There's no long-term contract required to get started, and you can cancel from your account settings.",
  },
  {
    q: "What businesses can use this?",
    a: "Any business where missed calls represent lost opportunities \u2014 home services, clinics and medical offices, salons and spas, auto shops, law firms, and similar phone-driven businesses.",
  },
];

export function FAQ() {
  return (
    <section id="faq" className="py-20">
      <div className="container-page max-w-prose">
        <h2 className="font-display text-3xl text-ink">Frequently asked questions</h2>
        <dl className="mt-8 space-y-6">
          {faqs.map((item) => (
            <div key={item.q} className="border-b border-line pb-6">
              <dt className="font-medium text-ink">{item.q}</dt>
              <dd className="mt-2 text-sm text-ink-soft">{item.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}

export function FinalCTA() {
  return (
    <section id="contact" className="border-t border-line py-20">
      <div className="container-page flex flex-col items-start justify-between gap-6 rounded-xl bg-brand-50 p-10 md:flex-row md:items-center">
        <div>
          <h2 className="font-display text-2xl text-ink">Ready to stop losing calls?</h2>
          <p className="mt-2 text-ink-soft">Set up takes minutes. No hardware, no new phone number.</p>
        </div>
        <div className="flex flex-wrap gap-4">
          <a href={`mailto:${SITE.demoContactEmail}?subject=Book a demo`}>
            <Button variant="secondary" className="px-6 py-3 text-base">
              Book a demo
            </Button>
          </a>
          <Link to={SITE.signupHref}>
            <Button className="px-6 py-3 text-base">Get started</Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
